import { Bot, Brain, Zap, Target, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import ConsultationModal from '@/components/ConsultationModal';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

const AISolutions = () => {
  const { t } = useLanguage();
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);

  const features = [
    {
      icon: Brain,
      title: t('aiSolutions.customAIModels'),
      description: t('aiSolutions.customAIDescription')
    },
    {
      icon: Zap,
      title: t('aiSolutions.processAutomation'), 
      description: t('aiSolutions.processAutomationDescription')
    },
    {
      icon: Target,
      title: t('aiSolutions.intelligentChatbots'),
      description: t('aiSolutions.intelligentChatbotsDescription')
    },
    {
      icon: ArrowRight,
      title: t('aiSolutions.predictiveAnalytics'),
      description: t('aiSolutions.predictiveAnalyticsDescription')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Back Button */}
      <section className="pt-24 pb-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <Link to="/">
            <Button variant="outline-light" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('jobs.backToHome')}
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Hero Section */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center animate-fade-in">
            <div className="inline-flex p-6 rounded-3xl bg-gradient-to-br from-primary to-primary-glow mb-8 glow-primary">
              <Bot className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold mb-6">
              {t('aiSolutions.title')}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
              {t('aiSolutions.heroDescription')}
            </p>
            <Button 
              size="lg" 
              className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
              onClick={() => setIsConsultationOpen(true)}
            >
              {t('aiSolutions.startJourney')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('aiSolutions.whatWeDeliver')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="group p-6 sm:p-8 rounded-3xl card-gradient border border-border/50 hover:border-primary/50 transition-smooth hover:scale-105 hover:glow-primary animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-primary to-primary-glow glow-accent mb-6">
                    <feature.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-display font-bold mb-4 text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('aiSolutions.ourAIProcess')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: t('aiSolutions.dataAnalysis'), description: t('aiSolutions.dataAnalysisDescription') },
              { step: "02", title: t('aiSolutions.modelDevelopment'), description: t('aiSolutions.modelDevelopmentDescription') },
              { step: "03", title: t('aiSolutions.integrationOptimization'), description: t('aiSolutions.integrationOptimizationDescription') }
            ].map((item, index) => (
              <div 
                key={item.step}
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.3}s` }}
              >
                <div className="text-6xl font-display font-bold gradient-text mb-4">
                  {item.step}
                </div>
                <h3 className="text-2xl font-display font-bold mb-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-display font-bold mb-6">
            {t('aiSolutions.readyToHarness')}
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            {t('aiSolutions.readyToHarnessDescription')}
          </p>
          <Button 
            size="lg" 
            className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
            onClick={() => setIsConsultationOpen(true)}
          >
            {t('aiSolutions.getFreeConsultation')}
          </Button>
        </div>
      </section>

      <Footer />
      
      <ConsultationModal 
        open={isConsultationOpen} 
        onOpenChange={setIsConsultationOpen} 
      />
    </div>
  );
};

export default AISolutions;